<template>
  <v-dialog
    v-model="isOpened"
    :fullscreen="props.fullscreen"
    :transition="transition"
    :class="{ wide, preventClickOverlay }"
    :content-class="['dialog__wrapper', { wide }]"
    class="dialog"
    :z-index="99999"
    @after-enter="afterEnter"
    @after-leave="afterLeave"
  >
    <template v-if="slots.activator" #activator="{ props: activatorProps }">
      <div class="d-flex" v-bind="activatorProps">
        <slot name="activator" />
      </div>
    </template>
    <div class="dialog__container" :class="{ center, noBorderRadius }" :style="{ width }">
      <div v-if="!hideTitle" class="dialog__title">
        <slot name="title" />
        <v-spacer />
        <slot v-if="!hideCloser" name="close" :close-dialog="closeDialog">
          <e-btn :icon="!$vuetify.display.lgAndUp" variant="context" size="M" @click="onCloseButtonClick">
            <span v-if="$vuetify.display.lgAndUp">Закрыть</span>
            <template #append>
              <e-icon name="closeSvg" />
            </template>
            <e-icon v-if="!$vuetify.display.lgAndUp" name="closeSvg" />
          </e-btn>
        </slot>
      </div>

      <div v-if="slots.secondaryTitle" class="dialog__secondary-title">
        <slot name="secondaryTitle" />
      </div>

      <div class="dialog__scroll-content">
        <div :class="[{ transparent: transparentContent, fullWithOnTablet, noContentPadding }, 'dialog__content']">
          <slot />
          <div v-if="slots.buttons" class="dialog__buttons">
            <slot name="buttons" />
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script lang="ts" setup>
import { toRef, watch } from 'vue';
import { EBtn, EIcon } from '@/core/components/ui';

const props = withDefaults(
  defineProps<{
    fullscreen?: boolean;
    left?: boolean;
    center?: boolean;
    bottom?: boolean;
    transparentContent?: boolean;
    fullWithOnTablet?: boolean;
    wide?: boolean;
    hideCloser?: boolean;
    hideTitle?: boolean;
    preventClickOverlay?: boolean;
    width?: string;
    noBorderRadius?: boolean;
    noContentPadding?: boolean;
  }>(),
  {
    fullscreen: true,
  },
);

const emit = defineEmits<{
  (event: 'open'): void;
  (event: 'close'): void;
  (event: 'afterEnter'): void;
  (event: 'afterLeave'): void;
  (event: 'closeButtonClick'): void;
}>();

const slots = defineSlots<{
  default: (props?: unknown) => unknown;
  activator?: (props?: unknown) => unknown;
  title?: (props?: unknown) => unknown;
  buttons?: (props?: unknown) => unknown;
  close?: (props: { closeDialog: () => void }) => unknown;
  secondaryTitle?: (props: unknown) => unknown;
}>();

const isOpened = defineModel<boolean>();

watch(isOpened, (value) => {
  if (!value) {
    emit('close');
  } else {
    emit('open');
  }
});

const closeDialog = () => {
  isOpened.value = false;
};

const onCloseButtonClick = () => {
  emit('closeButtonClick');
  closeDialog();
};

const showDialog = () => {
  isOpened.value = true;
};

const transition = toRef(() =>
  props.center
    ? 'v-fade-transition'
    : props.left
      ? 'slide-x-transition'
      : props.bottom
        ? 'dialog-bottom-transition'
        : 'slide-x-reverse-transition',
);

const afterEnter = () => {
  emit('afterEnter');
};

const afterLeave = () => {
  emit('afterLeave');
};

defineExpose({
  show: showDialog,
  close: closeDialog,
});
</script>

<style lang="scss" scoped>
:deep(.v-overlay__content.dialog__wrapper) {
  right: 0;
  left: auto;

  max-width: 100%;

  background-color: transparent !important;

  pointer-events: none;
}

:deep(.v-overlay__content.dialog__wrapper.wide) {
  .dialog__container {
    width: 100%;
  }

  @include md-and-up {
    width: calc(100% - 80px);
  }

  @include md-and-down {
    width: 100%;
    .dialog__container {
      border-radius: 0;
    }
  }

  @include xl {
    width: calc(100% - 302px);
  }
}

.dialog {
  &__container {
    display: flex;
    flex-direction: column;

    width: 704px;
    height: 100%;
    margin-left: auto;

    background-color: $main-bg-color;
    border-radius: $border-radius-xl 0 0 $border-radius-xl;

    pointer-events: all;

    &.noBorderRadius {
      border-radius: 0 !important;
    }

    @include lg-and-down {
      width: 600px;
    }

    @include sm-and-down {
      width: 100%;

      background-color: $body-bg-color;
      border-radius: 0;
    }

    &.center {
      margin: 24px auto;

      border-radius: $border-radius-xl;
    }
  }

  &.preventClickOverlay {
    :deep(.v-overlay__scrim) {
      pointer-events: none;
    }
  }

  &__title {
    display: flex;
    align-items: center;

    box-sizing: content-box;

    min-height: 44px;
    padding: 16px 32px;

    word-break: break-word;

    @include sm-and-down {
      padding: 10px 8px 8px 16px;
    }
  }

  &__secondary-title {
    margin-bottom: 16px;
    padding: 0 32px;

    @include sm-and-down {
      margin-top: 8px;
      margin-bottom: 8px;
      padding: 0 16px;
    }
  }

  &__scroll-content {
    display: flex;
    flex-direction: column;

    height: 100%;

    @include scrollbar();
  }

  &.v-dialog--fullscreen {
    .dialog__content {
      @include sm-and-down {
        border-radius: $border-radius-xl $border-radius-xl 0 0;
      }
    }
  }

  &__content {
    display: flex;
    flex-grow: 1;
    flex-direction: column;

    padding: 8px 32px 40px;

    border-radius: $border-radius-xl;

    &.noContentPadding {
      padding: 0 !important;
    }

    @include lg-and-down {
      padding: 24px;
    }

    @include sm-and-down {
      padding: 24px max(calc((100% - 640px) / 2), 24px);

      background-color: $main-bg-color;

      &.fullWithOnTablet {
        padding-right: 24px;
        padding-left: 24px;

        @include xs {
          padding-right: 16px;
          padding-left: 16px;
        }
      }
    }

    @include xs {
      padding: 24px 16px;
    }

    &.transparent {
      padding-top: 10px;
      padding-bottom: 10px;

      background: transparent;
      border-radius: 0;
    }
  }

  &__buttons {
    display: flex;
    flex-wrap: wrap;

    gap: 8px;

    margin-top: 24px;

    @include md-and-down {
      justify-content: flex-end;
    }

    @include sm-and-down {
      justify-content: center;

      margin-top: auto;
    }

    @include xs {
      gap: 16px;

      & > * {
        width: calc(50% - 8px);
      }
    }
  }
}
</style>
